import React, { useLayoutEffect, useState } from 'react';
import ProfaseModal from '../../../commons/ProfaseModal';
import PageForm, { FormField } from '../../../commons/PageForm';
import { useAppDispatch, useAppSelector } from '../../../store';
import { createEntity as createContacto } from '../../contacto.reducer';

const ACCENT = '#0ea5e9';

export interface ContactoCrearFormValues {
  nombre: string;
  apellido: string;
  fechaNacimiento: string;
  telefonoOficinaPrimario: string;
  telefonoOficinaSecundario: string;
  telefonoMovilPrimario: string;
  telefonoMovilSecundario: string;
  correoElectronicoPrimario: string;
  correoElectronicoSecundario: string;
}

function makeEmptyForm(): ContactoCrearFormValues {
  return {
    nombre: '',
    apellido: '',
    fechaNacimiento: '',
    telefonoOficinaPrimario: '',
    telefonoOficinaSecundario: '',
    telefonoMovilPrimario: '',
    telefonoMovilSecundario: '',
    correoElectronicoPrimario: '',
    correoElectronicoSecundario: '',
  };
}

const fields: FormField<ContactoCrearFormValues>[] = [
  { name: 'nombre', label: 'Nombre', required: true, placeholder: 'Nombre' },
  { name: 'apellido', label: 'Apellido', required: true, placeholder: 'Apellido' },
  { name: 'fechaNacimiento', label: 'Fecha de nacimiento', type: 'date' },
  { name: 'telefonoOficinaPrimario', label: 'Tel. oficina', placeholder: 'Tel. oficina' },
  { name: 'telefonoOficinaSecundario', label: 'Tel. oficina 2', placeholder: 'Tel. oficina 2' },
  { name: 'telefonoMovilPrimario', label: 'Tel. móvil', placeholder: 'Tel. móvil' },
  { name: 'telefonoMovilSecundario', label: 'Tel. móvil 2', placeholder: 'Tel. móvil 2' },
  { name: 'correoElectronicoPrimario', label: 'Correo electrónico', type: 'email', placeholder: 'correo@ejemplo.com' },
  { name: 'correoElectronicoSecundario', label: 'Correo electrónico 2', type: 'email', placeholder: 'correo2@ejemplo.com' },
];

export interface GrupoContactoCrearModalProps {
  open: boolean;
  grupoId?: string | null;
  /** Empresa seleccionada en la ruta (opcional en el alta). */
  empresaId?: string | null;
  onClose: () => void;
  onCreated?: () => void;
}

function trimOrEmpty(s: string) {
  return s.trim();
}

export default function GrupoContactoCrearModal({
  open,
  grupoId,
  empresaId,
  onClose,
  onCreated,
}: GrupoContactoCrearModalProps) {
  const dispatch = useAppDispatch();
  const updating = useAppSelector(s => s.contacto.updating);

  const [form, setForm] = useState<ContactoCrearFormValues>(() => makeEmptyForm());
  const [errors, setErrors] = useState<Partial<Record<keyof ContactoCrearFormValues, string>>>({});

  useLayoutEffect(() => {
    if (!open) return;
    setForm(makeEmptyForm());
    setErrors({});
  }, [open]);

  const handleFieldChange = (name: keyof ContactoCrearFormValues, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Partial<Record<keyof ContactoCrearFormValues, string>> = {};
    if (!trimOrEmpty(form.nombre)) errs.nombre = 'El nombre es requerido';
    if (!trimOrEmpty(form.apellido)) errs.apellido = 'El apellido es requerido';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const gid = Number(grupoId);
    if (Number.isNaN(gid)) {
      setErrors({ nombre: 'Grupo no válido' });
      return;
    }

    const nombre = trimOrEmpty(form.nombre);
    const apellido = trimOrEmpty(form.apellido);
    const contacto = [nombre, apellido].filter(Boolean).join(' ');

    const payload: Record<string, unknown> = {
      grupoId: gid,
      nombre,
      apellido,
      contacto,
    };

    if (trimOrEmpty(form.fechaNacimiento)) payload.fechaNacimiento = trimOrEmpty(form.fechaNacimiento);
    if (trimOrEmpty(form.telefonoOficinaPrimario)) payload.telefonoOficinaPrimario = trimOrEmpty(form.telefonoOficinaPrimario);
    if (trimOrEmpty(form.telefonoOficinaSecundario)) payload.telefonoOficinaSecundario = trimOrEmpty(form.telefonoOficinaSecundario);
    if (trimOrEmpty(form.telefonoMovilPrimario)) payload.telefonoMovilPrimario = trimOrEmpty(form.telefonoMovilPrimario);
    if (trimOrEmpty(form.telefonoMovilSecundario)) payload.telefonoMovilSecundario = trimOrEmpty(form.telefonoMovilSecundario);
    if (trimOrEmpty(form.correoElectronicoPrimario)) payload.correoElectronicoPrimario = trimOrEmpty(form.correoElectronicoPrimario);
    if (trimOrEmpty(form.correoElectronicoSecundario)) payload.correoElectronicoSecundario = trimOrEmpty(form.correoElectronicoSecundario);

    const eid = empresaId != null && empresaId !== 'new' ? Number(empresaId) : NaN;
    if (!Number.isNaN(eid)) payload.empresaId = eid;

    try {
      await dispatch(createContacto(payload)).unwrap();
      onCreated?.();
      onClose();
    } catch {
      /* 403 u otro error: interceptor / UI global */
    }
  };

  if (!open) return null;
  if (grupoId == null || grupoId === 'new') return null;

  return (
    <ProfaseModal
      open
      titleId="grupo-contacto-crear-modal-title"
      zIndex={100010}
      dialogStyle={{ maxWidth: 560, maxHeight: 'min(90vh, 720px)' }}
      onBackdropMouseDown={e => {
        if (e.target === e.currentTarget && !updating) onClose();
      }}
    >
      <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
        <h2 id="grupo-contacto-crear-modal-title" className="profase-modal-title m-0">
          Agregar contacto
        </h2>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm flex-shrink-0"
          onClick={onClose}
          disabled={updating}
          aria-label="Cerrar"
        >
          Cerrar
        </button>
      </div>
      <p className="profase-modal-description">
        Datos del nuevo contacto del grupo.
      </p>
      <PageForm
        embedded
        fields={fields}
        values={form}
        errors={errors}
        isNew
        saving={updating}
        labelInline
        onFieldChange={handleFieldChange}
        onSubmit={handleSubmit}
        onCancel={onClose}
        accentColor={ACCENT}
        submitLabel="Guardar contacto"
        cancelLabel="Cancelar"
      />
    </ProfaseModal>
  );
}
